import { clsx } from 'clsx';
import { BILL_CREATION_FIELDS } from '@/types/onboarding';
import { InfoTooltip } from '@components/common/InfoTooltip';

interface BillCreationFieldsStepProps {
  selectedFields: string[];
  onChange: (fields: string[]) => void;
}

export const BillCreationFieldsStep = ({ selectedFields, onChange }: BillCreationFieldsStepProps) => {
  const handleToggle = (key: string) => {
    const field = BILL_CREATION_FIELDS.find((f) => f.key === key);
    if (field?.alwaysOn) return;
    if (selectedFields.includes(key)) {
      onChange(selectedFields.filter((f) => f !== key));
    } else {
      onChange([...selectedFields, key]);
    }
  };

  return (
    <div>
      <div className="flex items-center gap-2">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Bill Creation Fields</h2>
        <InfoTooltip>Choose the fields included when processed invoices become bills.</InfoTooltip>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3">
        {BILL_CREATION_FIELDS.map((field) => {
          const isRequired = field.alwaysOn === true;
          const isSelected = isRequired || selectedFields.includes(field.key);

          return (
            <button
              key={field.key}
              type="button"
              disabled={isRequired}
              onClick={() => handleToggle(field.key)}
              className={clsx(
                'rounded-lg border p-3 text-left transition-colors',
                isSelected
                  ? 'border-primary-500 dark:border-primary-400 bg-primary-50 dark:bg-primary-900/20'
                  : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900',
                isRequired && 'cursor-not-allowed opacity-75'
              )}
            >
              <div className="flex items-center gap-2">
                <div
                  className={clsx(
                    'flex h-4 w-4 shrink-0 items-center justify-center rounded border',
                    isSelected
                      ? 'border-primary-500 bg-primary-500 dark:border-primary-400 dark:bg-primary-400'
                      : 'border-gray-300 dark:border-gray-600'
                  )}
                >
                  {isSelected && (
                    <svg className="h-3 w-3 text-white" viewBox="0 0 12 12" fill="none">
                      <path
                        d="M3.5 6L5.5 8L8.5 4"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  )}
                </div>
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {field.label}
                </span>
                {isRequired && (
                  <span className="ml-auto rounded bg-gray-200 dark:bg-gray-700 px-1.5 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400">
                    Required
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
